import type { ReactElement } from 'react';
import { useState } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import type { Option } from '../../../design/components/MultiChipSelect';
import { MultiChipSelect } from '../../../design/components/MultiChipSelect';

type ReactHookFormMultiChipSelectorProps<T extends FieldValues> = {
    readonly control: Control<T>;
    readonly label: string;
    readonly name: Path<T>;
    readonly options: ReadonlyArray<Option>;
};

export const ReactHookFormMultiChipSelectorField = <T extends FieldValues>({
    control,
    label,
    name,
    options,
    // biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration
}: ReactHookFormMultiChipSelectorProps<T>): ReactElement<any> => {
    const [selectedIds, setSelectedIds] = useState<readonly string[]>([]);

    return (
        <Controller
            control={control}
            name={name}
            render={({ field }) => {
                const value: readonly string[] = field.value ?? selectedIds;

                const toggleOption = (updateOptionKey: string): void => {
                    const updatedOptions =
                        value.includes(updateOptionKey) === true
                            ? value.filter((key) => key !== updateOptionKey)
                            : [...value, updateOptionKey];

                    setSelectedIds(updatedOptions);
                    field.onChange(updatedOptions);
                };

                const optionsWithSelection = options.map((option) => ({
                    ...option,
                    isSelected: value.includes(option.id),
                }));

                return <MultiChipSelect groupName={label} options={optionsWithSelection} onChange={toggleOption} />;
            }}
        />
    );
};
